import EditIcon from "@mui/icons-material/EditOutlined";
import { Avatar, Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { getDownloadURL } from "firebase/storage";
import { useRef, useState } from "react";
import { RaRecord, useRecordContext } from "react-admin";
import { setUserAvatarOverride } from "../api/backoffice.api";
import { gcs } from "../utils/storage";
import { LocalizedError } from "../utils/LocalizedError";
import { Form2 } from "./Form2";
import { cardTitleStyle, overviewCardStyle } from "./backofficeTheme";
import { useThemeColor } from "../hooks/useThemeColor";
import { LoadingIndicator } from "./LoadingIndicator";

export const UserAvatarForm = <T extends RaRecord<string>>() => {
  const user = useRecordContext<T>();
  const userId = user.id;
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const primaryColor = useThemeColor("primary");

  const persistAvatarOverride = async (avatarOverrideUrl: string) => {
    await setUserAvatarOverride(userId, avatarOverrideUrl);
  };

  return (
    <Box sx={overviewCardStyle}>
      <Form2>
        <Typography sx={cardTitleStyle}>Profilfoto</Typography>
        <Box sx={{ position: "relative", width: 100, height: 100, mb: "12px" }}>
          <Avatar src={previewUrl ?? user.avatarUrl} sx={{ width: 100, height: 100 }} />
          {loading && <LoadingIndicator />}
          <IconButton
            size="small"
            disabled={loading}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              backgroundColor: primaryColor.main,
              color: primaryColor.contrastText,
              width: 28,
              height: 28,
              "&:hover": { backgroundColor: primaryColor.dark },
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              fileInputRef.current?.click();
            }}
          >
            Bild hochladen
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setLoading(true);
              void (async () => {
                await runSafely(() => persistAvatarOverride(_avatarPlaceholderUrl), {
                  errorMessage: "Fehler beim Löschen des Profilfotos",
                  successMessage: "Profilfoto erfolgreich gelöscht.",
                  onSuccess: () => setPreviewUrl(_avatarPlaceholderUrl),
                  onError: () => setPreviewUrl(undefined),
                });
                setLoading(false);
              })();
            }}
          >
            Bild löschen
          </MenuItem>
        </Menu>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            const picked = e.target.files?.[0];
            e.target.value = "";
            if (!picked) return;
            setLoading(true);
            const url = URL.createObjectURL(picked);
            const img = new Image();
            img.onload = async () => {
              await runSafely(
                async () => {
                  if (picked.size > _maxAvatarFileSize) {
                    URL.revokeObjectURL(url);
                    throw new LocalizedError(`Die Datei ist zu groß (maximal ${_maxAvatarFileSize} Bytes).`);
                  }
                  if (img.width !== img.height) {
                    URL.revokeObjectURL(url);
                    throw new LocalizedError("Das Bild ist nicht quadratisch.");
                  }
                  setPreviewUrl(url);
                  const result = await gcs.uploadFile(
                    picked,
                    (ext) => `/users/${userId.toString()}/avatarOverride/avatar_override.${ext}`,
                  );
                  const downloadUrl = await getDownloadURL(result.ref);
                  await persistAvatarOverride(downloadUrl);
                  return downloadUrl;
                },
                {
                  errorMessage: "Fehler beim Hochladen des Profilfotos",
                  successMessage:
                    "Profilfoto erfolgreich gespeichert. Achtung: Profilfotos werden gecacht. Es kann bis zu 24 Stunden dauern, bis das neue Profilfoto überall angezeigt wird.",
                  onSuccess: (downloadUrl) => setPreviewUrl(downloadUrl),
                  onError: () => setPreviewUrl(undefined),
                },
              );
              setLoading(false);
            };
            img.src = url;
          }}
        />
      </Form2>
    </Box>
  );
};

const _maxAvatarFileSize = 500000;
const _avatarPlaceholderUrl = "https://img.autovio.de/avatar-placeholder.png";
